import React, { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import {
  AppBar,
  Box,
  Toolbar,
  IconButton,
  Typography,
  Menu,
  MenuItem,
  Container,
  Avatar,
  Button,
  Tooltip,
  Badge,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider
} from '@mui/material';
import {
  Menu as MenuIcon,
  Notifications as NotificationsIcon,
  Dashboard as DashboardIcon,
  DirectionsCar as VehiclesIcon,
  Person as DriversIcon,
  People as CustomersIcon,
  Route as RidesIcon,
  CalendarToday as CalendarIcon,
  EventNote as ShiftPlannerIcon,
  BarChart as ReportsIcon,
  Settings as SettingsIcon,
  Logout as LogoutIcon,
  ManageAccounts as ManageAccountsIcon,
  Forum as DriverMessagesIcon
} from '@mui/icons-material';
import { styled, alpha } from '@mui/material/styles';
import { isCentralRole, logoutUser } from '../../redux/slices/authSlice';
import { subscribeToNotifications, unsubscribeFromNotifications, markAllNotificationsAsRead, isCriticalNotification } from '../../redux/slices/notificationsSlice';
import brandingConfig from '../../config/brandingConfig';
import { getAssetPath } from '../../utils/assetManager';
import alertSoundService from '../../services/alertSoundService';
import NotificationCenter from '../notifications/NotificationCenter';
import DriverMessageCenter from '../driverMessages/DriverMessageCenter';

const StyledBadge = styled(Badge)(({ theme }) => ({
  '& .MuiBadge-badge': {
    backgroundColor: brandingConfig.colors.error,
    color: 'white',
  },
}));

// Die Leiste traegt die Markenfarbe. Schrift und Hover-Schleier muessen deren
// Helligkeit folgen — fest verdrahtetes Weiss steht auf einem Marken-Gelb
// praktisch unsichtbar da und faellt nur bei dunklen Marken nicht auf.
const NavButton = styled(Button)(({ theme }) => ({
  color: theme.palette.primary.contrastText,
  marginRight: theme.spacing(2),
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.contrastText, 0.1),
  },
}));

const LogoContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  textDecoration: 'none',
  marginRight: theme.spacing(2),
  cursor: 'pointer',
  overflow: 'hidden'
}));

const LogoImg = styled('img')(({ theme }) => ({
  height: 44,
  marginRight: theme.spacing(0)
}));

const SchriftzugImg = styled('img')(({ theme }) => ({
  height: 130,
  marginLeft: theme.spacing(-1),
  marginTop: theme.spacing(0.5),
  marginBottom: theme.spacing(-0.5)
}));

const pages = [
  { name: 'Dashboard', path: '/dashboard', icon: <DashboardIcon /> },
  { name: 'Fahrer', path: '/fahrer', icon: <DriversIcon /> },
  { name: 'Kunden', path: '/kunden', icon: <CustomersIcon /> },
  { name: 'Fahrzeuge', path: '/fahrzeuge', icon: <VehiclesIcon /> },
  { name: 'Fahrten', path: '/fahrten', icon: <RidesIcon /> },
  { name: 'Taxi-Kalender', path: '/schichten/kalender', icon: <CalendarIcon /> },
  { name: 'Schichtplaner', path: '/schichtplaner', icon: <ShiftPlannerIcon /> },
  { name: 'Berichte', path: '/berichte', icon: <ReportsIcon /> },
  { name: 'Einstellungen', path: '/einstellungen', icon: <SettingsIcon /> },
];

const getUserDisplayName = (user) => (
  user?.displayName || user?.name || user?.email || 'Benutzer'
);

const getUserInitial = (user) => {
  const displayName = getUserDisplayName(user).trim();
  return displayName ? displayName.charAt(0).toUpperCase() : 'U';
};

const Navbar = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  
  const { user } = useSelector((state) => state.auth);
  const { unreadCount, criticalCount, notifications } = useSelector((state) => state.notifications);
  
  // Remove anchorElNav to fix unused variable warning
  const [anchorElUser, setAnchorElUser] = useState(null);
  const [anchorElNotifications, setAnchorElNotifications] = useState(null);
  const [anchorElDriverMessages, setAnchorElDriverMessages] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Echtzeit-Listener für Benachrichtigungen, wenn Benutzer eingeloggt ist.
  // (Überfällige Vorbestellungen meldet der Server: portal_preorder_not_started —
  // das frühere client-seitige Polling via rideMonitoringService entfällt.)
  useEffect(() => {
    if (user) {
      dispatch(subscribeToNotifications({ userId: user.uid }));

      return () => {
        dispatch(unsubscribeFromNotifications());
      };
    } else {
      dispatch(unsubscribeFromNotifications());
    }
  }, [dispatch, user]);

  // Warnton bei neu eintreffenden kritischen Benachrichtigungen (aus dem
  // Firestore-Listener, funktioniert damit für alle serverseitigen Events)
  const seenCriticalIdsRef = useRef(null);

  useEffect(() => {
    // Bei Benutzerwechsel/Logout zurücksetzen, damit der erste Snapshot
    // des nächsten Logins nicht als "neu" alarmiert
    seenCriticalIdsRef.current = null;
  }, [user]);

  useEffect(() => {
    if (!user || !notifications) return;

    const criticalIds = notifications
      .filter((n) => !n.read && isCriticalNotification(n))
      .map((n) => n.id);

    if (seenCriticalIdsRef.current === null) {
      // Erste Snapshot-Lieferung nach Login: nur merken, nicht alarmieren
      seenCriticalIdsRef.current = new Set(criticalIds);
      return;
    }

    const hasNew = criticalIds.some((id) => !seenCriticalIdsRef.current.has(id));
    criticalIds.forEach((id) => seenCriticalIdsRef.current.add(id));

    if (hasNew) {
      alertSoundService.playAlertSound();
    }
  }, [user, notifications]);
  
  // Diese useEffect-Hook ist nicht mehr nötig, da wir die Adminsitzung beibehalten
  
  const handleOpenUserMenu = (event) => {
    setAnchorElUser(event.currentTarget);
  };
  
  const handleOpenNotificationsMenu = (event) => {
    setAnchorElNotifications(event.currentTarget);
    
    // NICHT automatisch als gelesen markieren beim Öffnen
    // Das verursacht einen State-Update der das Menu sofort schließt
    // Stattdessen nur beim Schließen markieren
  };

  const handleOpenDriverMessagesMenu = (event) => {
    setAnchorElDriverMessages(event.currentTarget);
  };

  // Removed handleCloseNavMenu to fix unused variable warning

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };
  
  const handleCloseNotificationsMenu = () => {
    // Markiere alle Benachrichtigungen als gelesen beim Schließen
    if (user && unreadCount > 0) {
      dispatch(markAllNotificationsAsRead(user.uid));
    }
    
    setAnchorElNotifications(null);
  };

  const handleCloseDriverMessagesMenu = () => {
    setAnchorElDriverMessages(null);
  };
  
  const toggleDrawer = (open) => (event) => {
    if (event.type === 'keydown' && (event.key === 'Tab' || event.key === 'Shift')) {
      return;
    }
    setDrawerOpen(open);
  };
  
  const handleLogout = () => {
    dispatch(logoutUser());
    navigate('/login');
    handleCloseUserMenu();
  };

  const DrawerList = () => (
    <Box
      sx={{ width: 250 }}
      role="presentation"
      onClick={toggleDrawer(false)}
      onKeyDown={toggleDrawer(false)}
    >
      <Box sx={{ p: 2, display: 'flex', alignItems: 'center', overflow: 'hidden', maxHeight: '100px' }}>
        <img 
          src={getAssetPath('icon', true)} 
          alt={`${brandingConfig.appName} Logo`} 
          style={{ height: 44, marginRight: 0 }} 
        />
        <img 
          src={getAssetPath('primary', true)} 
          alt={brandingConfig.appName} 
          style={{ height: 90, marginLeft: -4 }} 
        />
      </Box>
      <Divider />
      <List>
        {pages.map((page) => (
          <ListItem button key={page.name} component={Link} to={page.path}>
            <ListItemIcon>
              {page.icon}
            </ListItemIcon>
            <ListItemText primary={page.name} />
          </ListItem>
        ))}
      </List>
      <Divider />
      <List>
        <ListItem button onClick={handleLogout}>
          <ListItemIcon>
            <LogoutIcon />
          </ListItemIcon>
          <ListItemText primary="Abmelden" />
        </ListItem>
      </List>
    </Box>
  );

  return (
    <>
      <AppBar position="static">
        <Container maxWidth="xl">
          <Toolbar disableGutters sx={{ height: '64px', overflow: 'hidden' }}>
            {/* Desktop Logo */}
            <LogoContainer 
              component={Link}
              to="/"
              sx={{ display: { xs: 'none', md: 'flex' }, maxHeight: '64px' }}
            >
              <LogoImg 
                src={getAssetPath('icon', true)} 
                alt={`${brandingConfig.appName} Logo`} 
              />
              <SchriftzugImg 
                src={getAssetPath('primary', true)} 
                alt={brandingConfig.appName} 
              />
            </LogoContainer>

            {/* Mobile Menu */}
            <Box sx={{ display: { xs: 'flex', md: 'none' } }}>
              <IconButton
                size="large"
                aria-label="Menü öffnen"
                aria-controls="menu-appbar"
                aria-haspopup="true"
                onClick={toggleDrawer(true)}
                color="inherit"
              >
                <MenuIcon />
              </IconButton>
            </Box>
            
            {/* Mobile Logo */}
            <LogoContainer 
              component={Link}
              to="/"
              sx={{ display: { xs: 'flex', md: 'none' }, flexGrow: 1, justifyContent: 'flex-start', ml: 1 }}
            >
              <LogoImg 
                src={getAssetPath('icon', true)} 
                alt={`${brandingConfig.appName} Logo`} 
                sx={{ height: 44 }} 
              />
              <SchriftzugImg 
                src={getAssetPath('primary', true)} 
                alt={brandingConfig.appName} 
                sx={{ height: 130, marginLeft: -1 }} 
              />
            </LogoContainer>
            
            {/* Desktop Navigation */}
            <Box sx={{ flexGrow: 1, display: { xs: 'none', md: 'flex' } }}>
              {pages.map((page) => (
                <NavButton
                  key={page.name}
                  component={Link}
                  to={page.path}
                  startIcon={page.icon}
                >
                  {page.name}
                </NavButton>
              ))}
            </Box>

            {/* User and Notifications */}
            <Box sx={{ flexGrow: 0 }}>
              {user && (
                <>
                  <Tooltip title={criticalCount > 0 ? `⚠️ ${criticalCount} kritische Meldung(en)` : "Benachrichtigungen öffnen"}>
                    <IconButton
                      onClick={handleOpenNotificationsMenu}
                      sx={{
                        p: 1,
                        mr: 1,
                        ...(criticalCount > 0 && {
                          animation: 'pulse 2s infinite',
                          '@keyframes pulse': {
                            '0%': {
                              transform: 'scale(1)',
                            },
                            '50%': {
                              transform: 'scale(1.05)',
                            },
                            '100%': {
                              transform: 'scale(1)',
                            },
                          },
                        })
                      }}
                      color="inherit"
                    >
                      <StyledBadge
                        badgeContent={unreadCount}
                        color="error"
                        sx={{
                          '& .MuiBadge-badge': {
                            backgroundColor: criticalCount > 0 ? '#ff4444' : brandingConfig.colors.error,
                            animation: criticalCount > 0 ? 'blink 1.5s infinite' : 'none',
                            '@keyframes blink': {
                              '0%': { opacity: 1 },
                              '50%': { opacity: 0.3 },
                              '100%': { opacity: 1 },
                            },
                          }
                        }}
                      >
                        <NotificationsIcon sx={{
                          color: criticalCount > 0 ? '#ff4444' : 'inherit'
                        }} />
                      </StyledBadge>
                    </IconButton>
                  </Tooltip>

                  <Tooltip title="Fahrer-Nachrichten">
                    <IconButton
                      onClick={handleOpenDriverMessagesMenu}
                      sx={{ p: 1, mr: 1 }}
                      color="inherit"
                      aria-label="Fahrer-Nachrichten öffnen"
                    >
                      <DriverMessagesIcon />
                    </IconButton>
                  </Tooltip>
                  
                  <Tooltip title="Einstellungen öffnen">
                    <IconButton onClick={handleOpenUserMenu} sx={{ p: 0 }}>
                      <Avatar 
                        alt={getUserDisplayName(user)} 
                        src={user.photoURL || ''} 
                        sx={{ bgcolor: 'grey.900', color: 'common.white' }}
                      >
                        {getUserInitial(user)}
                      </Avatar>
                    </IconButton>
                  </Tooltip>
                </>
              )}
              
              {/* Notifications Menu */}
              <Menu
                sx={{ mt: '45px' }}
                id="menu-notifications"
                anchorEl={anchorElNotifications}
                anchorOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                keepMounted
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                open={Boolean(anchorElNotifications)}
                onClose={handleCloseNotificationsMenu}
                PaperProps={{
                  sx: {
                    mt: 1.5,
                    minWidth: { xs: 'calc(100vw - 24px)', sm: 400 },
                    maxWidth: { xs: 'calc(100vw - 24px)', sm: 500 },
                    maxHeight: 600,
                    overflow: 'auto',
                    borderRadius: 2,
                    boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
                  }
                }}
              >
                <NotificationCenter
                  onClose={handleCloseNotificationsMenu}
                />
              </Menu>

              {/* Driver Messages Menu */}
              <Menu
                sx={{ mt: '45px' }}
                id="menu-driver-messages"
                anchorEl={anchorElDriverMessages}
                anchorOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                keepMounted
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                open={Boolean(anchorElDriverMessages)}
                onClose={handleCloseDriverMessagesMenu}
                PaperProps={{
                  sx: {
                    mt: 1.5,
                    minWidth: { xs: 'calc(100vw - 24px)', sm: 420 },
                    maxWidth: { xs: 'calc(100vw - 24px)', sm: 520 },
                    maxHeight: 680,
                    overflow: 'auto',
                    borderRadius: 2,
                    boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
                  }
                }}
              >
                <DriverMessageCenter
                  user={user}
                />
              </Menu>
              
              {/* User Menu */}
              <Menu
                sx={{ mt: '45px' }}
                id="menu-appbar"
                anchorEl={anchorElUser}
                anchorOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                keepMounted
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                open={Boolean(anchorElUser)}
                onClose={handleCloseUserMenu}
              >
                <MenuItem component={Link} to="/profil" onClick={handleCloseUserMenu}>
                  <Typography textAlign="center">Profil</Typography>
                </MenuItem>
                {isCentralRole(user) && (
                  <MenuItem component={Link} to="/profil/unternehmer" onClick={handleCloseUserMenu}>
                    <ManageAccountsIcon fontSize="small" sx={{ mr: 1 }} />
                    <Typography textAlign="center">Unternehmer & Sub-Konten</Typography>
                  </MenuItem>
                )}
                <MenuItem component={Link} to="/einstellungen" onClick={handleCloseUserMenu}>
                  <Typography textAlign="center">Einstellungen</Typography>
                </MenuItem>
                <MenuItem onClick={handleLogout}>
                  <Typography textAlign="center">Abmelden</Typography>
                </MenuItem>
              </Menu>
            </Box>
          </Toolbar>
        </Container>
      </AppBar>
      
      {/* Mobile Drawer */}
      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={toggleDrawer(false)}
      >
        <DrawerList />
      </Drawer>
    </>
  );
};

export default Navbar;
